import { DIALER_SKIP_REASONS, type DialerLead, type DialerSkipReason, type DialerSource } from "@gioia365/contracts";
import { formatNumericTime } from "../i18n/format";
import type { Translate } from "../i18n/locale";

// Le parole della cascata che servono a più finestre (piano M5, Task 5; D16): la sorgente, il nome di un Lead, perché un Lead
// si salta da solo, il motivo di un salto.

/**
 * Chi è chi nel nome di una sorgente dei Task: la persona di chi guarda (di cui il nome non si dice) e i nomi delle altre persone
 * (la lettura di «Di chi», `useSourcePeople`).
 */
export type SourcePeople = {
  me: string | null;
  nameOf(membershipId: string): string | undefined;
};

/** Nessuna persona da nominare: per una Smart List. */
export const NO_SOURCE_PEOPLE: SourcePeople = { me: null, nameOf: () => undefined };

/**
 * Il nome della sorgente di una cascata (barra, riepilogo, finestra «Hai una cascata in corso su …»), nella lingua di chi guarda: «Smart
 * List «…»» con il nome della lista di quando la sessione è nata o, da R4 (piano M5, Task 21), «Task «Oggi»» o «Task «Scaduti»» con la
 * scheda nella lingua di chi guarda e, per i Task di tutto il team o di un collega, «· Tutto il team» o «· Luca Verdi»: i Task di chi
 * guarda restano senza persona. Non porta virgolette attorno a sé: le frasi dei Task lo dicono senza.
 */
export function sourceText(t: Translate, session: { source: DialerSource; sourceName: string }, people: SourcePeople = NO_SOURCE_PEOPLE): string {
  const { source } = session;
  if (source.type === "smart_list") return t("dialer.sourceSmartList", { name: session.sourceName });
  const view = t(`tasks.views.${source.view}`);
  if (source.who === people.me) return t("dialer.sourceTasks", { view });
  // Una persona che non si ritrova fra quelle di «Di chi» (sospesa) resta senza nome: soltanto la scheda.
  const who = source.who === "all" ? t("tasks.wholeTeam") : people.nameOf(source.who);
  return who === undefined ? t("dialer.sourceTasks", { view }) : t("dialer.sourceTasksWho", { view, who });
}

/** Il nome di un Lead della cascata: i titolari, altrimenti il salone. */
export function dialerLeadName(lead: Pick<DialerLead, "displayName" | "salonName">): string {
  return lead.displayName?.trim() || lead.salonName?.trim() || "—";
}

/** Il nome con il salone, come nell'elenco: «Giulia Ferri · Salone Aurora». */
export function dialerLeadLine(lead: Pick<DialerLead, "displayName" | "salonName">): string {
  const name = dialerLeadName(lead);
  const salon = lead.salonName?.trim();
  return salon && salon !== name ? `${name} · ${salon}` : name;
}

/**
 * Perché un Lead si salta da solo, con il collega al telefono o che lo lavora o, per il richiamo concordato, l'ora
 * dell'accordo nel fuso dell'azienda (`timeZone`, quello della sessione).
 */
export function passoverText(t: Translate, passover: NonNullable<DialerLead["passover"]>, timeZone: string | null): string {
  return t(`dialer.passover.${passover.reason}`, {
    name: passover.byDisplayName ?? "",
    time: passover.at !== null && timeZone !== null ? formatNumericTime(timeZone, passover.at) : "",
  });
}

/**
 * Quanti Lead restano nella lista (D6, mock-up `m-session-summary`): quelli da chiamare e quelli che per ora si saltano da
 * soli, dalla coda riletta dal server. `fromTasks`: la cascata è partita dai Task, e restano «nei Task».
 */
export function summaryLeftText(t: Translate, toCall: number, passed: number, fromTasks = false): string {
  const suffix = fromTasks ? "Tasks" : "";
  if (toCall + passed === 0) return t(`dialer.summaryLeftNone${suffix}`);
  const calls = toCall > 0 ? t("dialer.summaryToCall", { count: toCall }) : t("dialer.summaryToCallNone");
  const detail =
    passed === 0 ? calls : passed === 1 ? t("dialer.summaryAndPassedOne", { toCall: calls }) : t("dialer.summaryAndPassed", { toCall: calls, count: passed });
  return toCall + passed === 1 ? t(`dialer.summaryLeftOne${suffix}`, { detail }) : t(`dialer.summaryLeft${suffix}`, { total: toCall + passed, detail });
}

const SKIP_REASONS: ReadonlySet<string> = new Set(DIALER_SKIP_REASONS);

/** Il motivo di «Salta», con le parole della finestra; un motivo che non si conosce vale «altro motivo». */
export function skipReasonText(t: Translate, reason: string | null): string {
  return t(`dialer.skipReasons.${reason !== null && SKIP_REASONS.has(reason) ? (reason as DialerSkipReason) : "other"}`);
}
